/**
 * Trust Level Badge Component
 * Visual indicator of security trust level
 */

import React from 'react';
import { TrustLevelBadgeProps } from '../types';

const TRUST_LEVEL_INFO = {
  0: {
    label: 'Trusted',
    color: 'green',
    bgColor: '#dcfce7',
    textColor: '#16a34a',
    description: 'Scripts and actions execute normally',
  },
  1: {
    label: 'Untrusted',
    color: 'red',
    bgColor: '#fee2e2',
    textColor: '#dc2626',
    description: 'Scripts blocked, only safe actions allowed',
  },
  2: {
    label: 'Explicit Trust',
    color: 'orange',
    bgColor: '#fef3c7',
    textColor: '#d97706',
    description: 'All actions require user approval',
  },
};

/**
 * Badge showing trust level with color coding
 * Accessible with ARIA labels and keyboard support
 */
export const TrustLevelBadge: React.FC<TrustLevelBadgeProps> = ({
  level,
  showLabel = true,
  className = '',
}: TrustLevelBadgeProps) => {
  const info = TRUST_LEVEL_INFO[level];

  return (
    <div
      className={`trust-level-badge ${className}`}
      role="status"
      aria-label={`Trust level: ${info.label}. ${info.description}`}
      aria-live="polite"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 12px',
        backgroundColor: info.bgColor,
        color: info.textColor,
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: '600',
        border: `1px solid ${info.textColor}`,
      }}
      title={info.description}
    >
      <span
        style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: info.textColor,
        }}
        aria-hidden="true"
      />
      {showLabel && (
        <span role="img" aria-label={info.label}>
          {info.label}
        </span>
      )}
    </div>
  );
};
